import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import {
    Building2, Search, Loader2, Handshake, CheckCircle2, AlertTriangle, Clock, ArrowUpDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import ExportExcelButton from '../components/ExportExcelButton';
import CompanyManagerDrawer from '../components/reconciliation/CompanyManagerDrawer';
import CompanyHistoryDrawer from '../components/reconciliation/CompanyHistoryDrawer';
import ReconcileModal from '../components/reconciliation/ReconcileModal';
import api from '../utils/api';
import { AuthContext } from '../contexts/AuthContext';
import { canAccessBossModules, normalizeRole } from '../utils/roleUtils';
import { formatRecordDate } from '../utils/dateUtils';
import {
    REVIEW_FILTERS,
    REVIEW_SORTS,
    formatBalance,
    formatDaysAgo,
    formatInterval,
    getDifferenceMeta,
    getReviewStatusMeta,
    matchesReviewFilter,
    reconciliationExcelMapper,
    sortCompanies
} from '../utils/reconciliationUtils';

const emptySummary = { total: 0, overdue: 0, soon: 0, ok: 0, never: 0, openDifferences: 0 };

// Satirin sol seridi: durumu uzaktan okunur kilar (renk tek basina anlam tasimaz; rozet de var)
const ACCENT_BY_STATUS = {
    overdue: 'border-l-red-500',
    soon: 'border-l-amber-500',
    never: 'border-l-blue-500',
    ok: 'border-l-transparent',
    inactive: 'border-l-transparent'
};

const Reconciliations = () => {
    const { user } = useContext(AuthContext);
    const normalizedRole = normalizeRole(user?.role);
    const canView = canAccessBossModules(normalizedRole);
    const isAdmin = normalizedRole === 'admin';

    const [companies, setCompanies] = useState([]);
    const [summary, setSummary] = useState(emptySummary);
    const [today, setToday] = useState('');
    const [defaultIntervalDays, setDefaultIntervalDays] = useState(90);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [sort, setSort] = useState('urgency');
    const [isCompanyDrawerOpen, setIsCompanyDrawerOpen] = useState(false);
    const [activeCompanyId, setActiveCompanyId] = useState(null);
    const [reconcileTarget, setReconcileTarget] = useState(null);

    const fetchOverview = useCallback(async () => {
        try {
            const response = await api.get('/reconciliations/overview');
            setCompanies(response.data.companies || []);
            setSummary(response.data.summary || emptySummary);
            setToday(response.data.today || '');
            setDefaultIntervalDays(response.data.defaultIntervalDays || 90);
        } catch (error) {
            toast.error('Mutabakat verileri yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) {
            fetchOverview();
        }
    }, [canView, fetchOverview]);

    const visibleCompanies = useMemo(() => {
        const term = search.trim().toLocaleLowerCase('tr-TR');

        const filtered = companies.filter((company) => {
            if (!matchesReviewFilter(company, filter)) {
                return false;
            }

            return !term || company.name.toLocaleLowerCase('tr-TR').includes(term);
        });

        return sortCompanies(filtered, sort);
    }, [companies, search, filter, sort]);

    const stalest = useMemo(
        () => companies.find((company) => company.reviewStatus === 'overdue') || null,
        [companies]
    );

    const activeCompany = companies.find((company) => company.id === activeCompanyId) || null;

    if (!canView) {
        return <Navigate to="/dashboard" />;
    }

    const summaryCards = [
        { key: 'overdue', label: 'Gecikmiş', value: summary.overdue, textClass: 'tone-danger-text', hint: 'süresi geçti' },
        { key: 'soon', label: 'Yaklaşan', value: summary.soon, textClass: 'tone-warning-text', hint: 'yakında vadesi dolacak' },
        { key: 'ok', label: 'Güncel', value: summary.ok, textClass: 'tone-success-text', hint: 'mutabakat güncel' },
        { key: 'never', label: 'Hiç yapılmadı', value: summary.never, textClass: 'tone-info-text', hint: 'ilk mutabakat bekliyor' }
    ];

    return (
        <Layout>
            {/* HEADER */}
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-xl font-bold tracking-tight text-dark-text md:text-3xl">Mutabakat</h1>
                    <p className="text-sm text-dark-muted">Firmalarla cari hesap mutabakat durumu</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <ExportExcelButton
                        data={visibleCompanies}
                        mapper={reconciliationExcelMapper}
                        sheetName="Mutabakat"
                        filename={`Mutabakat_${today || 'liste'}.xlsx`}
                    />
                    <button
                        onClick={() => setIsCompanyDrawerOpen(true)}
                        className="flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-primary-500/20 transition-all hover:bg-primary-500"
                    >
                        <Building2 size={18} /> Firma Defteri
                    </button>
                </div>
            </div>

            {/* UYARILAR */}
            {!isLoading && (summary.overdue > 0 || summary.never > 0) && (
                <div className="mb-4 space-y-2">
                    {summary.overdue > 0 && stalest && (
                        <div className="tone-danger flex items-start gap-2 rounded-xl border px-4 py-2.5 text-sm">
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            <span>
                                <strong>{summary.overdue} firma</strong> ile mutabakat süresi geçti. En çok geciken:{' '}
                                <strong>{stalest.name}</strong> — son mutabakat {formatDaysAgo(stalest.daysSince).toLowerCase()}
                                {' '}({Math.abs(stalest.daysUntilDue)} gün gecikme).
                            </span>
                        </div>
                    )}
                    {summary.never > 0 && (
                        <div className="tone-info flex items-start gap-2 rounded-xl border px-4 py-2.5 text-sm">
                            <Clock size={16} className="mt-0.5 shrink-0" />
                            <span><strong>{summary.never} firma</strong> ile henüz hiç mutabakat yapılmadı.</span>
                        </div>
                    )}
                </div>
            )}

            {/* ÖZET KARTLARI (tıklayınca filtreler) */}
            <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                {summaryCards.map((card) => (
                    <button
                        key={card.key}
                        onClick={() => setFilter((current) => (current === card.key ? 'all' : card.key))}
                        className={`rounded-2xl border bg-dark-card p-4 text-left transition-colors hover:bg-dark-hover ${
                            filter === card.key ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-dark-border'
                        }`}
                    >
                        <p className="text-xs text-dark-muted">{card.label}</p>
                        <p className={`mt-1 text-2xl font-bold ${card.textClass}`}>{card.value}</p>
                        <p className="text-[11px] text-dark-muted">{card.hint}</p>
                    </button>
                ))}
            </div>

            {/* FİLTRE ÇUBUĞU */}
            <div className="mb-6 flex flex-col gap-4 rounded-xl border border-dark-border bg-dark-card p-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Firma ara..."
                        className="w-full rounded-lg border border-dark-border bg-dark-bg py-2 pl-10 pr-4 text-dark-text focus:border-primary-500 focus:outline-none"
                    />
                </div>
                <div className="flex flex-wrap gap-1 rounded-lg border border-dark-border bg-dark-bg/50 p-1">
                    {REVIEW_FILTERS.map((item) => (
                        <button
                            key={item.value}
                            onClick={() => setFilter(item.value)}
                            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                                filter === item.value ? 'bg-primary-500/15 text-primary-500' : 'text-dark-muted hover:text-dark-text'
                            }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                <div className="relative">
                    <ArrowUpDown size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" />
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        aria-label="Sıralama"
                        className="w-full cursor-pointer rounded-lg border border-dark-border bg-dark-bg py-2 pl-9 pr-3 text-sm text-dark-text focus:border-primary-500 focus:outline-none"
                    >
                        {REVIEW_SORTS.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* TABLO */}
            <div className="overflow-hidden rounded-2xl border border-dark-border bg-dark-card shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full whitespace-nowrap text-left text-sm">
                        <thead className="border-b border-dark-border bg-dark-bg/60 text-[11px] uppercase tracking-wider text-dark-muted md:text-xs">
                            <tr>
                                <th className="p-3 sm:p-4">Firma</th>
                                <th className="p-4 hidden sm:table-cell">Son Mutabakat</th>
                                <th className="p-4 hidden lg:table-cell text-right">Bizim / Karşı</th>
                                <th className="p-4 hidden md:table-cell text-right">Fark</th>
                                <th className="p-3 sm:p-4">Durum</th>
                                <th className="p-3 sm:p-4 text-right"><span className="sr-only">İşlem</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border text-dark-text">
                            {isLoading && (
                                <tr>
                                    <td colSpan="6" className="p-10 text-center text-dark-muted">
                                        <Loader2 size={20} className="mx-auto animate-spin" aria-label="Yükleniyor" />
                                    </td>
                                </tr>
                            )}

                            {!isLoading && visibleCompanies.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="p-10 text-center text-dark-muted">
                                        {companies.length === 0 ? (
                                            <span className="flex flex-col items-center gap-2">
                                                <Handshake size={28} />
                                                Henüz firma yok. Firma Defteri’nden firma ekleyin.
                                            </span>
                                        ) : 'Filtreye uyan firma bulunamadı.'}
                                    </td>
                                </tr>
                            )}

                            {!isLoading && visibleCompanies.map((company) => {
                                const status = getReviewStatusMeta(company);
                                const last = company.lastEvent;
                                const diff = last ? getDifferenceMeta(last) : null;

                                return (
                                    <tr
                                        key={company.id}
                                        onClick={() => setActiveCompanyId(company.id)}
                                        className="cursor-pointer transition-colors hover:bg-dark-hover"
                                    >
                                        <td className={`border-l-4 p-3 sm:p-4 whitespace-normal ${ACCENT_BY_STATUS[company.reviewStatus] || 'border-l-transparent'}`}>
                                            <span className="font-medium">{company.name}</span>
                                            <span className="block text-[11px] text-dark-muted">
                                                {formatInterval(company.intervalDays)} • {company.defaultCurrency}
                                            </span>
                                            <span className="block text-[11px] text-dark-muted sm:hidden">
                                                {last ? `${formatRecordDate(last.reconciledDate)} • ${formatDaysAgo(company.daysSince).toLowerCase()}` : 'Hiç mutabakat yok'}
                                            </span>
                                        </td>
                                        <td className="p-4 hidden sm:table-cell">
                                            {last ? (
                                                <>
                                                    <span>{formatRecordDate(last.reconciledDate)}</span>
                                                    <span className="block text-[11px] text-dark-muted">{formatDaysAgo(company.daysSince)}</span>
                                                </>
                                            ) : (
                                                <span className="text-dark-muted">—</span>
                                            )}
                                        </td>
                                        <td className="p-4 hidden lg:table-cell text-right text-dark-muted">
                                            {last && !last.balancesMissing ? (
                                                <>
                                                    <span className="text-dark-text">{formatBalance(last.ourBalance, last.currency)}</span>
                                                    <span className="block text-[11px]">{formatBalance(last.theirBalance, last.currency)}</span>
                                                </>
                                            ) : '—'}
                                        </td>
                                        <td className="p-4 hidden md:table-cell text-right">
                                            {diff && diff.tone !== 'muted' ? (
                                                <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${diff.className}`}>
                                                    {diff.tone === 'mismatch' && <AlertTriangle size={12} />}
                                                    {diff.label}
                                                </span>
                                            ) : (
                                                <span className="text-dark-muted">—</span>
                                            )}
                                        </td>
                                        <td className="p-3 sm:p-4">
                                            <span className={`inline-flex flex-wrap items-center gap-x-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${status.className}`}>
                                                {status.label}
                                                {status.detail && <span className="font-normal opacity-90">• {status.detail}</span>}
                                            </span>
                                        </td>
                                        <td className="p-3 sm:p-4 text-right" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => setReconcileTarget(company)}
                                                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                                                    status.urgent
                                                        ? 'bg-primary-600 text-white hover:bg-primary-500'
                                                        : 'border border-dark-border bg-dark-bg/50 text-dark-muted hover:text-dark-text'
                                                }`}
                                                title={`${company.name} ile mutabakat yap`}
                                                aria-label={`${company.name} ile mutabakat yap`}
                                            >
                                                <CheckCircle2 size={14} /> <span className="hidden sm:inline">Mutabakat Yap</span>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <CompanyManagerDrawer
                isOpen={isCompanyDrawerOpen}
                onClose={() => setIsCompanyDrawerOpen(false)}
                onChanged={fetchOverview}
                canDelete={isAdmin}
                defaultIntervalDays={defaultIntervalDays}
            />

            {activeCompany && (
                <CompanyHistoryDrawer
                    company={activeCompany}
                    today={today}
                    onClose={() => setActiveCompanyId(null)}
                    onChanged={fetchOverview}
                />
            )}

            <ReconcileModal
                isOpen={Boolean(reconcileTarget)}
                company={reconcileTarget}
                today={today}
                onClose={() => setReconcileTarget(null)}
                onDone={fetchOverview}
            />
        </Layout>
    );
};

export default Reconciliations;
